/**
 * The AI's filters, merged into a surface's stored filters in the shape that
 * surface keeps them (spec 2026-10-18 AI companion growth architecture, 4.4:
 * one filter contract for every panel).
 *
 * A panel's own Apply stores the form's shape, `[{ AND: [...rows] }]`, whose rows
 * keep what the query shape drops: a row hidden with the eye, a time-slice
 * window. Panels saved before that hold a query group, `{ AND: [...] }`. The
 * merge keeps whichever shape it finds, so the rows on columns the AI did not
 * name come back exactly as they were. The leaves it adds are in the query
 * shape, which the form reads too.
 */

type Node = Record<string, unknown>

/** The column a top-level leaf filters on, in the query shape or the form's; null for a group. */
function leafColumn(node: unknown): string | null {
  if (!node || typeof node !== 'object') return null
  const n = node as Node
  if (Array.isArray(n.AND) || Array.isArray(n.OR)) return null
  const left = n.left as { column?: unknown } | undefined
  const column = left?.column ?? n.column
  return typeof column === 'string' ? column : null
}

function isAndGroup(node: unknown): node is { AND: unknown[] } {
  return !!node && typeof node === 'object' && Array.isArray((node as Node).AND)
}

/**
 * `stored` with `leaves` added: each column a leaf names replaces that column's
 * top-level rows; every other row stays as it was. A root that is not an AND (an
 * OR group) is kept whole and ANDed with the new leaves, and a nested group is
 * never reached into. With nothing stored, the result is the form's shape, as a
 * panel's own Apply writes it.
 */
export function mergeFilterLeaves(stored: unknown, leaves: unknown[]): unknown {
  const named = new Set(leaves.map(leafColumn).filter((c): c is string => c !== null))
  const keep = (nodes: unknown[]) => nodes.filter((n) => !named.has(leafColumn(n) ?? ''))

  if (stored == null) return [{ AND: leaves }]
  if (Array.isArray(stored)) {
    if (stored.length === 1 && isAndGroup(stored[0]))
      return [{ ...(stored[0] as Node), AND: [...keep(stored[0].AND), ...leaves] }]
    // A legacy flat array is an AND of leaves; anything else (an OR root,
    // several groups) is kept whole.
    if (stored.every((n) => leafColumn(n) !== null)) return [{ AND: [...keep(stored), ...leaves] }]
    return [{ AND: [...stored, ...leaves] }]
  }
  if (isAndGroup(stored)) return { ...(stored as Node), AND: [...keep(stored.AND), ...leaves] }
  if (leafColumn(stored) !== null) return { AND: [...keep([stored]), ...leaves] }
  return { AND: [stored, ...leaves] }
}
